import { useState, MouseEvent } from 'react';
import { useSnackbar } from 'notistack';
import {
  Dialog, IconButton, TextField, Popover,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useApi } from 'lib/contexts/ApplicationState';
import { Track } from 'lib/types';
import { PostToImport } from 'lib/types/posts';
import { PostList } from './PostList';

interface Props {
  networkName: string;
  posts: PostToImport[];
  username: string;
  hasMorePages: boolean;
  postsAreLoading: boolean;
  onLoadMore: () => void;
  onNext: (result: any, importName: string, importId?: string) => void;
  onBack: () => void;
  onClose: () => void;

}

export const ImportPosts = ({
  networkName, username, posts, onLoadMore, onNext, onBack, onClose, hasMorePages, postsAreLoading,
}: Props) => {
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();

  const defaultTime = new Date().toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric',
  });
  const defaultName = `@${username} on ${networkName} - ${defaultTime}`;

  const [importName, setImportName] = useState(defaultName);

  const [selectedPostIds, setSelectedPostIds] = useState<string[]>([]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const [isLoading, setIsLoading] = useState(false);

  const selectPost = (postId: string) => {
    setSelectedPostIds([
      ...selectedPostIds,
      postId,
    ]);
  };

  const removePost = (postId: string) => {
    const removed = selectedPostIds.filter((p) => p !== postId);
    setSelectedPostIds(removed);
  };

  const addName = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const performImport = async () => {
    const postsToImport = posts.filter((p: PostToImport) => selectedPostIds.includes(p.sourceId));
    setIsLoading(true);
    let trackedImport: Track | undefined;
    try {
      trackedImport = await API.importPosts(postsToImport, importName);
      onNext(postsToImport, trackedImport.publicId, trackedImport.publicId);
    } catch (err: any) {
      if (err.toString().match(/Thumbnail/)) {
        onNext(postsToImport, trackedImport?.publicId || '', trackedImport?.publicId);
        enqueueSnackbar(err.toString(), {
          variant: 'info',
        });
      } else {
        enqueueSnackbar('There was an error importing some of your posts', {
          variant: 'error',
        });
      }
      setIsLoading(false);
    }
  };

  return (
    <Dialog onClose={onClose} fullWidth maxWidth="xl" open aria-labelledby="title" scroll="paper">
      <div className="flex justify-between items-center w-full">
        <div className="flex">
          <div className="p-2">
            <IconButton aria-label="close" onClick={onClose}>
              <Close />
            </IconButton>
          </div>
          <div id="title" className="text-xl py-4">
            <span className="font-bold">
              Select
              {' '}
              {networkName}
              {' '}
              Posts
              {' '}
            </span>
            (use checkboxes then click
            {' '}
            Next)
          </div>
        </div>
        <div className="mr-4">
          <button type="button" className="btn-primary-outlined mr-2" onClick={onBack}>
            Back
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={addName}
            disabled={selectedPostIds.length <= 0}
          >
            Next
          </button>
          <Popover
            PaperProps={{
              sx: {
                maxWidth: 320,
              },
            }}
            id="name-popover"
            open={open}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <div className="p-4">
              <div>
                <div className="font-semibold">Choose a name for your import</div>
                <div className="text-sm">
                  This will be visible only to you, and can help you
                  select this group of posts for bulk tagging or crediting.
                </div>
              </div>
              <TextField
                onChange={(e) => setImportName(e.target.value)}
                margin="normal"
                id="importname"
                label="Import Name"
                value={importName}
                disabled={isLoading || postsAreLoading}
                fullWidth
              />
              <button
                type="button"
                className="btn-primary"
                onClick={performImport}
                disabled={isLoading || postsAreLoading}
              >
                {isLoading ? 'Importing...' : 'Import' }
              </button>
            </div>
          </Popover>
        </div>
      </div>
      <div className="px-4 overflow-scroll h-full">
        <PostList
          posts={posts}
          selectedPostIds={selectedPostIds}
          onSelectPost={selectPost}
          onRemovePost={removePost}
        />
        <div className="my-6 justify-center flex-col items-center text-center self-center w-full">
          {hasMorePages && (
            <button type="button" className="btn-primary" onClick={onLoadMore} disabled={postsAreLoading || isLoading}>
              {postsAreLoading ? 'Loading More...' : 'More Posts'}
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
};
